package br.com.htms.inspeccaocampo.ui.kit

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.PlayArrow
import androidx.compose.material.icons.filled.Refresh
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.PathEffect
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.drawText
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.rememberTextMeasurer
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import br.com.htms.inspeccaocampo.data.colaboradores.DatasColab
import br.com.htms.inspeccaocampo.platform.CredenciaisNavegador
import br.com.htms.inspeccaocampo.ui.theme.PortalCores
import kotlin.math.abs
import kotlin.math.roundToLong

/** Componentes de dashboard do portal: gráficos, filtros de período e modo apresentação. */

object CoresDash {
    private val base = listOf(
        0xFF1E5AA8, 0xFFE07B00, 0xFF1B7F3B, 0xFF6A4FB3, 0xFFC62828, 0xFF0097A7, 0xFFF2B33D, 0xFF5B6575
    )
    val opcoes = listOf(
        0xFF1E5AA8, 0xFF0D47A1, 0xFF1565C0, 0xFF0097A7, 0xFF00695C, 0xFF1B7F3B, 0xFF7CB342,
        0xFFF2B33D, 0xFFE07B00, 0xFFE65100, 0xFFC62828, 0xFFAD1457, 0xFF6A4FB3, 0xFF6A1B9A,
        0xFF5D4037, 0xFF37474F, 0xFF5B6575
    )
    var porNome by mutableStateOf(mapOf<String, Long>())
    val paleta: List<Color> get() = base.map { Color(it) }
    fun cor(i: Int): Color = paleta[((i % paleta.size) + paleta.size) % paleta.size]
    fun corLong(i: Int): Long = base[((i % base.size) + base.size) % base.size]
    fun de(nome: String, padrao: Long): Color = Color(porNome[nome] ?: padrao)
    fun atual(nome: String, padrao: Long): Long = porNome[nome] ?: padrao
    fun definir(nome: String, argb: Long) { porNome = porNome + (nome to argb) }

    val bom = PortalCores.Verde
    val atencao = Color(0xFFE0A000)
    val ruim = PortalCores.Vermelho
    val neutro = PortalCores.CinzaClaro
}

object FmtDash {
    fun num(v: Double, casas: Int = 0): String {
        var fator = 1L
        repeat(casas) { fator *= 10 }
        val arred = (abs(v) * fator).roundToLong()
        val inteiro = (arred / fator).toString().reversed().chunked(3).joinToString(".").reversed()
        val dec = if (casas > 0) "," + (arred % fator).toString().padStart(casas, '0') else ""
        return (if (v < 0 && arred != 0L) "-" else "") + inteiro + dec
    }

    fun qtd(v: Double): String = if (v % 1.0 == 0.0) num(v, 0) else num(v, 1)
    fun qtd(v: Int): String = num(v.toDouble(), 0)
    fun pct(v: Double?, casas: Int = 0): String = v?.let { num(it * 100, casas) + "%" } ?: "—"

    fun moeda(v: Double): String = "R$ " + num(v, 2)

    /** Valores grandes abreviados (1,2 mil; 3,4 mi) para eixos e rótulos. */
    fun curto(v: Double): String = when {
        abs(v) >= 1_000_000 -> num(v / 1_000_000, 1) + " mi"
        abs(v) >= 10_000 -> num(v / 1_000, 1) + " mil"
        else -> qtd(v)
    }
}

// ---------- Datas e períodos ----------

enum class PeriodoDash(val rotulo: String) {
    D30("30 dias"),
    D90("90 dias"),
    ANO("Este ano"),
    M12("12 meses"),
    TUDO("Tudo")
}

/** Faixa de datas ISO (inclusive) usada como coluna dos gráficos. */
data class FaixaDash(val rotulo: String, val ini: String, val fim: String)

object DashDatas {
    private const val DIA_MS = 86_400_000L
    private const val FUSO_BR_MS = 3 * 3_600_000L
    private val MESES = listOf("jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez")

    fun hoje(): String = DatasColab.isoDeDias(DatasColab.hoje())

    fun deMs(ms: Long): String = DatasColab.isoDeDias((ms - FUSO_BR_MS).floorDiv(DIA_MS))

    /** Início do dia (Brasília) em epoch ms. */
    fun msDe(iso: String): Long? = DatasColab.diasDeIso(iso)?.let { it * DIA_MS + FUSO_BR_MS }

    fun add(iso: String, dias: Int): String = DatasColab.diasDeIso(iso)?.let { DatasColab.isoDeDias(it + dias) } ?: iso

    /** Aceita aaaa-mm-dd ou dd/mm/aaaa; "" se inválida. */
    fun normalizar(txt: String): String {
        val t = txt.trim()
        if (t.isEmpty()) return ""
        if (Regex("^\\d{4}-\\d{2}-\\d{2}").containsMatchIn(t)) return t.take(10).takeIf { DatasColab.diasDeIso(it) != null }.orEmpty()
        return DatasColab.brParaIso(t).takeIf { it.isNotEmpty() && DatasColab.diasDeIso(it) != null }.orEmpty()
    }

    fun br(iso: String): String = DatasColab.isoParaBr(iso).ifBlank { iso }

    fun mesRotulo(iso: String, comAno: Boolean = true): String {
        val m = iso.substring(5, 7).toIntOrNull() ?: return iso
        return MESES[(m - 1).coerceIn(0, 11)] + if (comAno) "/" + iso.substring(2, 4) else ""
    }

    private fun primeiroDoMes(iso: String) = iso.take(8) + "01"

    private fun mesSeguinte(iso: String): String {
        val y = iso.take(4).toInt()
        val m = iso.substring(5, 7).toInt()
        return if (m == 12) "${y + 1}-01-01" else "$y-${(m + 1).toString().padStart(2, '0')}-01"
    }

    private fun meses(ini: String, fim: String, comAno: Boolean): List<FaixaDash> {
        val out = mutableListOf<FaixaDash>()
        var a = primeiroDoMes(ini)
        while (a <= fim && out.size < 240) {
            val prox = mesSeguinte(a)
            out += FaixaDash(mesRotulo(a, comAno), a, add(prox, -1))
            a = prox
        }
        return out
    }

    /** Data inicial do período (null = sem limite). */
    fun inicio(p: PeriodoDash, hoje: String = hoje()): String? = when (p) {
        PeriodoDash.D30 -> add(hoje, -29)
        PeriodoDash.D90 -> add(hoje, -90)
        PeriodoDash.ANO -> hoje.take(4) + "-01-01"
        PeriodoDash.M12 -> {
            var x = primeiroDoMes(hoje)
            repeat(11) { x = primeiroDoMes(add(x, -1)) }
            x
        }
        PeriodoDash.TUDO -> null
    }

    /**
     * Colunas do período: dias (30 dias), semanas (90 dias), meses (ano / 12 meses / tudo) ou anos
     * quando "tudo" passa de 36 meses. [maisAntiga] define o começo de "tudo".
     */
    fun faixas(p: PeriodoDash, maisAntiga: String?, hoje: String = hoje()): List<FaixaDash> = when (p) {
        PeriodoDash.D30 -> (29 downTo 0).map { k -> add(hoje, -k).let { d -> FaixaDash(d.substring(8, 10) + "/" + d.substring(5, 7), d, d) } }
        PeriodoDash.D90 -> {
            val ini = add(hoje, -90)
            (0 until 13).map { k ->
                val a = add(ini, k * 7)
                val b = if (k == 12) hoje else add(a, 6)
                FaixaDash(a.substring(8, 10) + "/" + a.substring(5, 7), a, b)
            }
        }
        PeriodoDash.ANO -> meses(hoje.take(4) + "-01-01", hoje, comAno = false)
        PeriodoDash.M12 -> meses(inicio(p, hoje)!!, hoje, comAno = true)
        PeriodoDash.TUDO -> {
            val ini = maisAntiga?.takeIf { it.isNotBlank() && it <= hoje } ?: inicio(PeriodoDash.M12, hoje)!!
            val ms = meses(ini, hoje, comAno = true)
            if (ms.size <= 36) ms
            else (ini.take(4).toInt()..hoje.take(4).toInt()).map { y -> FaixaDash(y.toString(), "$y-01-01", "$y-12-31") }
        }
    }

    fun indice(faixas: List<FaixaDash>, iso: String): Int {
        if (iso.isBlank()) return -1
        for (i in faixas.indices) if (iso >= faixas[i].ini && iso <= faixas[i].fim) return i
        return -1
    }

    fun noPeriodo(iso: String, p: PeriodoDash, hoje: String = hoje()): Boolean {
        if (iso.isBlank()) return false
        val ini = inicio(p, hoje) ?: return true
        return iso >= ini
    }
}

/** Conta itens por faixa a partir da data ISO de cada um. */
fun <T> contarPorFaixa(itens: List<T>, faixas: List<FaixaDash>, data: (T) -> String, peso: (T) -> Double = { 1.0 }): List<Double> {
    val out = DoubleArray(faixas.size)
    itens.forEach { i -> DashDatas.indice(faixas, data(i)).takeIf { it >= 0 }?.let { out[it] += peso(i) } }
    return out.toList()
}

// ---------- Estrutura ----------

/**
 * Painel de dashboard: cabeçalho com Atualizar/Apresentar, filtros e conteúdo.
 * O mesmo [conteudo] é usado em tela cheia no modo apresentação (grande = true).
 */
@OptIn(ExperimentalLayoutApi::class)
@Composable
fun PainelDashboard(
    titulo: String,
    subtitulo: String = "",
    carregando: Boolean = false,
    erro: String? = null,
    onAtualizar: (() -> Unit)? = null,
    camposCor: List<Pair<String, Long>> = emptyList(),
    filtros: @Composable ColumnScope.() -> Unit = {},
    conteudo: @Composable ColumnScope.(largo: Boolean, grande: Boolean) -> Unit
) {
    var apresentar by remember { mutableStateOf(false) }
    var coresAberto by remember { mutableStateOf(false) }
    if (apresentar) {
        Dialog(onDismissRequest = { apresentar = false }, properties = DialogProperties(usePlatformDefaultWidth = false)) {
            BoxWithConstraints(Modifier.fillMaxSize().background(PortalCores.FundoPainel)) {
                val largo = maxWidth >= 900.dp
                val rolagem244 = androidx.compose.foundation.rememberScrollState()
                br.com.htms.inspeccaocampo.ui.kit.AreaRolavel(rolagem244, Modifier.fillMaxSize()) {
                Column( Modifier.fillMaxSize().verticalScroll(rolagem244).padding(if (largo) 32.dp else 14.dp),
                    verticalArrangement = Arrangement.spacedBy(18.dp)
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Column(Modifier.weight(1f)) {
                            Text(
                                titulo,
                                style = if (largo) MaterialTheme.typography.headlineMedium else MaterialTheme.typography.titleLarge,
                                fontWeight = FontWeight.Bold,
                                color = PortalCores.Navy
                            )
                            if (subtitulo.isNotBlank()) Text(subtitulo, style = MaterialTheme.typography.titleMedium, color = PortalCores.CinzaTexto)
                        }
                        IconButton(onClick = { apresentar = false }) {
                            Icon(Icons.Filled.Close, contentDescription = "Fechar apresentação", tint = PortalCores.Navy)
                        }
                    }
                    filtros()
                    conteudo(largo, true)
                }
                }
            }
        }
    }
    BoxWithConstraints(Modifier.fillMaxWidth()) {
        val largo = maxWidth >= 860.dp
        Column(verticalArrangement = Arrangement.spacedBy(18.dp)) {
            CartaoBranco(Modifier.fillMaxWidth()) {
                Column(Modifier.fillMaxWidth().padding(18.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Column(Modifier.weight(1f)) {
                            Text(titulo, color = PortalCores.Navy, fontWeight = FontWeight.Bold, style = MaterialTheme.typography.titleLarge)
                            if (subtitulo.isNotBlank()) Text(subtitulo, color = PortalCores.CinzaTexto, style = MaterialTheme.typography.bodySmall)
                        }
                        if (carregando) {
                            CircularProgressIndicator(Modifier.size(22.dp), strokeWidth = 2.dp)
                            Spacer(Modifier.width(10.dp))
                        }
                        if (onAtualizar != null) {
                            IconButton(onClick = onAtualizar, enabled = !carregando) {
                                Icon(Icons.Filled.Refresh, contentDescription = "Atualizar", tint = PortalCores.Navy)
                            }
                        }
                        if (camposCor.isNotEmpty()) TextButton(onClick = { coresAberto = true }) { Text("Cores") }
                        PortalOutlinedButton(onClick = { apresentar = true }) {
                            Icon(Icons.Filled.PlayArrow, contentDescription = null, modifier = Modifier.size(18.dp))
                            if (largo) {
                                Spacer(Modifier.width(6.dp))
                                Text("Apresentar")
                            }
                        }
                    }
                    filtros()
                }
            }
            if (erro != null) AvisoFaixa(erro, PortalCores.Vermelho) {}
            conteudo(largo, false)
        }
    }
    if (coresAberto) {
        Dialog(onDismissRequest = { coresAberto = false }) {
            CartaoBranco(Modifier.widthIn(max = 560.dp)) {
                val rolagem = rememberScrollState()
                Column(
                    Modifier.padding(16.dp).heightIn(max = 520.dp).verticalScroll(rolagem),
                    verticalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    Text("Cores dos gráficos", color = PortalCores.Navy, fontWeight = FontWeight.Bold)
                    Text(
                        "Escolha a cor de cada campo deste painel.",
                        color = PortalCores.CinzaTexto,
                        style = MaterialTheme.typography.bodySmall
                    )
                    camposCor.forEach { (nome, padrao) ->
                        val atual = CoresDash.atual(nome, padrao)
                        Text(nome, style = MaterialTheme.typography.labelMedium, color = PortalCores.Navy)
                        FlowRow(horizontalArrangement = Arrangement.spacedBy(6.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
                            CoresDash.opcoes.forEach { hex ->
                                Box(
                                    Modifier
                                        .size(26.dp)
                                        .background(Color(hex), RoundedCornerShape(6.dp))
                                        .border(
                                            if (hex == atual) 3.dp else 1.dp,
                                            if (hex == atual) PortalCores.Navy else PortalCores.Cartao,
                                            RoundedCornerShape(6.dp)
                                        )
                                        .clickable { CoresDash.definir(nome, hex) }
                                )
                            }
                        }
                    }
                    TextButton(onClick = { coresAberto = false }) { Text("Fechar") }
                }
            }
        }
    }
}

@Composable
fun BotaoCores(campos: List<Pair<String, Long>>) {
    var aberto by remember { mutableStateOf(false) }
    TextButton(onClick = { aberto = true }) { Text("Cores") }
    if (aberto) {
        Dialog(onDismissRequest = { aberto = false }) {
            EditorCores(campos) { aberto = false }
        }
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun EditorCores(campos: List<Pair<String, Long>>, onFechar: () -> Unit) {
    CartaoBranco(Modifier.widthIn(max = 560.dp)) {
        val rolagem = rememberScrollState()
        Column(
            Modifier.padding(16.dp).heightIn(max = 520.dp).verticalScroll(rolagem),
            verticalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            Text("Cores", color = PortalCores.Navy, fontWeight = FontWeight.Bold)
            Text("Escolha a cor de cada campo.", color = PortalCores.CinzaTexto, style = MaterialTheme.typography.bodySmall)
            campos.forEach { (nome, padrao) ->
                val atual = CoresDash.atual(nome, padrao)
                Text(nome, style = MaterialTheme.typography.labelMedium, color = PortalCores.Navy)
                FlowRow(horizontalArrangement = Arrangement.spacedBy(6.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
                    CoresDash.opcoes.forEach { hex ->
                        Box(
                            Modifier.size(26.dp).background(Color(hex), RoundedCornerShape(6.dp))
                                .border(if (hex == atual) 3.dp else 1.dp, if (hex == atual) PortalCores.Navy else PortalCores.Cartao, RoundedCornerShape(6.dp))
                                .clickable { CoresDash.definir(nome, hex) }
                        )
                    }
                }
            }
            TextButton(onClick = onFechar) { Text("Fechar") }
        }
    }
}

/** Seletor de período em chips. */
@OptIn(ExperimentalLayoutApi::class)
@Composable
fun FiltroPeriodo(
    periodo: PeriodoDash,
    onPeriodo: (PeriodoDash) -> Unit,
    opcoes: List<PeriodoDash> = PeriodoDash.entries
) {
    FlowRow(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
        opcoes.forEach { p -> ChipFiltro(p.rotulo, p == periodo) { onPeriodo(p) } }
    }
}

@Composable
fun ChipFiltro(texto: String, selecionado: Boolean, onClick: () -> Unit) {
    Text(
        texto,
        color = if (selecionado) Color.White else PortalCores.Navy,
        fontWeight = if (selecionado) FontWeight.SemiBold else FontWeight.Normal,
        style = MaterialTheme.typography.bodyMedium,
        modifier = Modifier
            .background(if (selecionado) PortalCores.Marca else PortalCores.Cartao, RoundedCornerShape(18.dp))
            .border(1.dp, if (selecionado) PortalCores.Marca else PortalCores.BordaCampo, RoundedCornerShape(18.dp))
            .clickable(onClick = onClick)
            .padding(horizontal = 14.dp, vertical = 7.dp)
    )
}

/** Filtros aplicados por clique nos gráficos, com "x" para remover cada um. */
@OptIn(ExperimentalLayoutApi::class)
@Composable
fun FiltrosAtivos(filtros: List<Pair<String, () -> Unit>>, onLimpar: () -> Unit) {
    if (filtros.isEmpty()) return
    FlowRow(
        horizontalArrangement = Arrangement.spacedBy(8.dp),
        verticalArrangement = Arrangement.spacedBy(6.dp)
    ) {
        Text("Filtrando:", style = MaterialTheme.typography.bodyMedium, color = PortalCores.CinzaTexto)
        filtros.forEach { (rotulo, remover) ->
            Row(
                Modifier
                    .background(PortalCores.AzulClaro, RoundedCornerShape(18.dp))
                    .border(1.dp, PortalCores.AzulIcone.copy(alpha = 0.5f), RoundedCornerShape(18.dp))
                    .clickable(onClick = remover)
                    .padding(start = 12.dp, end = 8.dp, top = 5.dp, bottom = 5.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(rotulo, color = PortalCores.Navy, style = MaterialTheme.typography.bodyMedium, fontWeight = FontWeight.SemiBold)
                Spacer(Modifier.width(4.dp))
                Icon(Icons.Filled.Close, contentDescription = "Remover filtro", tint = PortalCores.Navy, modifier = Modifier.size(16.dp))
            }
        }
        if (filtros.size > 1) {
            Text(
                "Limpar tudo",
                color = PortalCores.AzulIcone,
                style = MaterialTheme.typography.bodyMedium,
                modifier = Modifier.clickable(onClick = onLimpar).padding(6.dp)
            )
        }
    }
}

/** Blocos lado a lado na tela larga (pesos iguais) ou empilhados. */
@Composable
fun LinhaDash(largo: Boolean, vararg blocos: @Composable (Modifier) -> Unit) {
    if (largo) {
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(14.dp)) {
            blocos.forEach { it(Modifier.weight(1f)) }
        }
    } else {
        Column(verticalArrangement = Arrangement.spacedBy(14.dp)) { blocos.forEach { it(Modifier.fillMaxWidth()) } }
    }
}

/** Cartão de um dashboard. [inteiro] ocupa a linha toda; os demais entram em pares na tela larga. */
class ItemPainel(
    val id: String,
    val titulo: String,
    val inteiro: Boolean = false,
    val conteudo: @Composable (Modifier) -> Unit
)

private data class PrefPainel(val id: String, val visivel: Boolean, val colunas: Int? = null)

private data class SlotPainel(val item: ItemPainel, val span: Int)

/** Ordem e visibilidade dos cartões, gravadas neste aparelho. */
private object DisposicaoDash {
    var versao by mutableStateOf(0)
    private val cache = HashMap<String, String>()

    fun ler(chave: String): String {
        versao
        return cache.getOrPut(chave) {
            runCatching { CredenciaisNavegador.lerLocal("icd_dash_$chave") }.getOrNull().orEmpty()
        }
    }

    fun gravar(chave: String, valor: String) {
        cache[chave] = valor
        runCatching { CredenciaisNavegador.gravarLocal("icd_dash_$chave", valor.ifBlank { null }) }
        versao++
    }

    fun prefs(chave: String, ids: List<String>): List<PrefPainel> {
        val salvo = ler(chave)
        if (salvo.isBlank()) return ids.map { PrefPainel(it, true) }
        val conhecidos = ids.toSet()
        val out = mutableListOf<PrefPainel>()
        val vistos = mutableSetOf<String>()
        salvo.split(',').forEach { raw ->
            val t = raw.trim()
            if (t.isEmpty()) return@forEach
            val visivel = !t.startsWith("-")
            val corpo = t.removePrefix("-")
            val id = corpo.substringBefore('=')
            val colunas = corpo.substringAfter('=', "").toIntOrNull()?.takeIf { it == 2 || it == 3 || it == 6 }
            if (id !in conhecidos || id in vistos) return@forEach
            vistos += id
            out += PrefPainel(id, visivel, colunas)
        }
        ids.filter { it !in vistos }.forEach { out += PrefPainel(it, true) }
        return out
    }

    fun codificar(prefs: List<PrefPainel>): String =
        prefs.joinToString(",") { p ->
            val tam = p.colunas?.let { "=$it" }.orEmpty()
            (if (p.visivel) p.id else "-${p.id}") + tam
        }
}

/**
 * Desenha os cartões na ordem escolhida pelo usuário.
 * [organizar] mostra Personalizar; no modo apresentação o botão fica oculto, mas a ordem salva continua valendo.
 */
@Composable
fun ColumnScope.PaineisDoDash(
    chave: String,
    largo: Boolean,
    organizar: Boolean,
    itens: List<ItemPainel>
) {
    val ids = itens.map { it.id }
    val assinatura = ids.joinToString("\u0000")
    var prefs by remember(chave, assinatura) { mutableStateOf(DisposicaoDash.prefs(chave, ids)) }
    var editando by remember(chave) { mutableStateOf(false) }
    fun aplicar(nova: List<PrefPainel>) {
        prefs = nova
        DisposicaoDash.gravar(chave, DisposicaoDash.codificar(nova))
    }
    val porId = itens.associateBy { it.id }
    val slots = slotsDe(prefs, porId)
    if (organizar && editando) {
        EditorNaTela(
            largo = largo,
            prefs = prefs,
            slots = slots,
            titulos = itens.associate { it.id to it.titulo },
            onMover = { id, delta -> moverVisivel(prefs, id, delta)?.let { aplicar(it) } },
            onOcultar = { id -> aplicar(prefs.map { if (it.id == id) it.copy(visivel = false) else it }) },
            onMostrar = { id -> aplicar(prefs.map { if (it.id == id) it.copy(visivel = true) else it }) },
            onTamanho = { id, span -> aplicar(prefs.map { if (it.id == id) it.copy(colunas = span) else it }) },
            onRestaurar = {
                prefs = ids.map { PrefPainel(it, true) }
                DisposicaoDash.gravar(chave, "")
            },
            onConcluir = { editando = false }
        )
        return
    }
    if (organizar) {
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
            PortalOutlinedButton(onClick = { editando = true }) { Text("Personalizar") }
        }
    }
    if (slots.isEmpty()) {
        Text("Nenhum painel visível. Use Personalizar para exibir os itens.", color = PortalCores.CinzaTexto)
    }
    gradePaineis(largo, slots) { slot, m ->
        Box(m) { slot.item.conteudo(Modifier.fillMaxWidth()) }
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun ColumnScope.EditorNaTela(
    largo: Boolean,
    prefs: List<PrefPainel>,
    slots: List<SlotPainel>,
    titulos: Map<String, String>,
    onMover: (String, Int) -> Unit,
    onOcultar: (String) -> Unit,
    onMostrar: (String) -> Unit,
    onTamanho: (String, Int) -> Unit,
    onRestaurar: () -> Unit,
    onConcluir: () -> Unit
) {
    val ocultos = prefs.filter { !it.visivel }
    CartaoBranco(Modifier.fillMaxWidth()) {
        Column(Modifier.fillMaxWidth().padding(16.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(4.dp)) {
                    Text("Personalizar layout", color = PortalCores.Navy, fontWeight = FontWeight.Bold, style = MaterialTheme.typography.titleMedium)
                    Text(
                        "Subir e Descer mudam a ordem. 1/3, 1/2 e Inteira mudam a largura. Ocultar tira o painel. A escolha fica neste aparelho.",
                        color = PortalCores.CinzaTexto,
                        style = MaterialTheme.typography.bodySmall
                    )
                    if (!largo) {
                        Text(
                            "Nesta largura os painéis ficam um embaixo do outro. A largura escolhida vale na tela larga.",
                            color = PortalCores.CinzaTexto,
                            style = MaterialTheme.typography.bodySmall
                        )
                    }
                }
                TextButton(onClick = onRestaurar) { Text("Restaurar") }
                PortalButton(onClick = onConcluir) { Text("Concluir") }
            }
            if (ocultos.isNotEmpty()) {
                Text("Ocultos", color = PortalCores.Navy, fontWeight = FontWeight.SemiBold)
                FlowRow(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    ocultos.forEach { pref ->
                        BotaoAjuste(titulos[pref.id] ?: pref.id, prefixo = "+") { onMostrar(pref.id) }
                    }
                }
            }
        }
    }
    if (slots.isEmpty()) {
        Text("Nenhum painel na tela. Escolha um item em Ocultos.", color = PortalCores.CinzaTexto)
        return
    }
    gradePaineis(largo, slots) { slot, m ->
        val n = slots.indexOf(slot) + 1
        CartaoAjuste(
            titulo = slot.item.titulo,
            posicao = n,
            total = slots.size,
            span = slot.span,
            onSubir = { onMover(slot.item.id, -1) },
            onDescer = { onMover(slot.item.id, 1) },
            onTamanho = { onTamanho(slot.item.id, it) },
            onOcultar = { onOcultar(slot.item.id) },
            modifier = m
        )
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun CartaoAjuste(
    titulo: String,
    posicao: Int,
    total: Int,
    span: Int,
    onSubir: () -> Unit,
    onDescer: () -> Unit,
    onTamanho: (Int) -> Unit,
    onOcultar: () -> Unit,
    modifier: Modifier
) {
    Column(
        modifier
            .border(1.dp, PortalCores.AzulIcone, RoundedCornerShape(12.dp))
            .background(PortalCores.AzulClaro, RoundedCornerShape(12.dp))
            .padding(12.dp),
        verticalArrangement = Arrangement.spacedBy(10.dp)
    ) {
        Text(
            "$posicao. $titulo",
            color = PortalCores.Navy,
            fontWeight = FontWeight.Bold,
            maxLines = 2,
            overflow = TextOverflow.Ellipsis,
            style = MaterialTheme.typography.titleMedium
        )
        FlowRow(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
            BotaoAjuste("Subir", enabled = posicao > 1, onClick = onSubir)
            BotaoAjuste("Descer", enabled = posicao < total, onClick = onDescer)
            BotaoAjuste("1/3", marcado = span == 2, onClick = { onTamanho(2) })
            BotaoAjuste("1/2", marcado = span == 3, onClick = { onTamanho(3) })
            BotaoAjuste("Inteira", marcado = span == 6, onClick = { onTamanho(6) })
            BotaoAjuste("Ocultar", onClick = onOcultar)
        }
    }
}

@Composable
private fun BotaoAjuste(
    texto: String,
    enabled: Boolean = true,
    marcado: Boolean = false,
    prefixo: String? = null,
    onClick: () -> Unit
) {
    val rotulo = if (prefixo == null) texto else "$prefixo $texto"
    val fundo = when {
        !enabled -> PortalCores.CabecalhoTabela
        marcado -> PortalCores.AzulIcone
        else -> PortalCores.Cartao
    }
    val tinta = when {
        !enabled -> PortalCores.CinzaClaro
        marcado -> Color.White
        else -> PortalCores.AzulIcone
    }
    Text(
        rotulo,
        modifier = Modifier
            .background(fundo, RoundedCornerShape(8.dp))
            .border(1.dp, if (enabled) PortalCores.AzulIcone else PortalCores.Borda, RoundedCornerShape(8.dp))
            .clickable(enabled = enabled, onClick = onClick)
            .padding(horizontal = 12.dp, vertical = 8.dp),
        color = tinta,
        fontWeight = FontWeight.SemiBold,
        style = MaterialTheme.typography.bodyMedium
    )
}

private fun moverVisivel(prefs: List<PrefPainel>, id: String, delta: Int): List<PrefPainel>? {
    val visiveis = prefs.filter { it.visivel }
    val i = visiveis.indexOfFirst { it.id == id }
    val j = i + delta
    if (i < 0 || j !in visiveis.indices) return null
    val ia = prefs.indexOfFirst { it.id == visiveis[i].id }
    val ib = prefs.indexOfFirst { it.id == visiveis[j].id }
    if (ia < 0 || ib < 0) return null
    val lista = prefs.toMutableList()
    val item = lista[ia]
    lista[ia] = lista[ib]
    lista[ib] = item
    return lista
}

private fun slotsDe(prefs: List<PrefPainel>, porId: Map<String, ItemPainel>): List<SlotPainel> =
    prefs.mapNotNull { p ->
        if (!p.visivel) return@mapNotNull null
        val item = porId[p.id] ?: return@mapNotNull null
        val span = when (p.colunas) {
            2, 3, 6 -> p.colunas
            else -> if (item.inteiro) 6 else 3
        }
        SlotPainel(item, span)
    }

private fun empacotar(slots: List<SlotPainel>): List<List<SlotPainel>> {
    val out = mutableListOf<List<SlotPainel>>()
    var atual = mutableListOf<SlotPainel>()
    var usado = 0
    slots.forEach { slot ->
        val span = slot.span.coerceIn(2, 6)
        if (atual.isNotEmpty() && usado + span > 6) {
            out += atual.toList()
            atual = mutableListOf()
            usado = 0
        }
        atual += slot
        usado += span
        if (usado >= 6) {
            out += atual.toList()
            atual = mutableListOf()
            usado = 0
        }
    }
    if (atual.isNotEmpty()) out += atual.toList()
    return out
}

@Composable
private fun ColumnScope.gradePaineis(
    largo: Boolean,
    slots: List<SlotPainel>,
    celula: @Composable (SlotPainel, Modifier) -> Unit
) {
    val linhas = if (largo) empacotar(slots) else slots.map { listOf(it) }
    linhas.forEach { linha ->
        val usado = linha.sumOf { it.span.coerceIn(2, 6) }
        if (!largo || (linha.size == 1 && usado >= 6)) {
            celula(linha[0], Modifier.fillMaxWidth())
        } else {
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(14.dp)) {
                linha.forEach { celula(it, Modifier.weight(it.span.coerceIn(2, 6).toFloat())) }
                val sobra = 6 - usado
                if (sobra > 0) Spacer(Modifier.weight(sobra.toFloat()))
            }
        }
    }
}

@Composable
fun SemDadosDash(titulo: String, texto: String) {
    CartaoSecao(titulo = titulo, subtitulo = texto) {}
}

/** Linha de KPIs que quebra em 2 colunas no celular em vez de empilhar tudo. */
@Composable
fun GradeKpis(largo: Boolean, kpis: List<@Composable (Modifier) -> Unit>) {
    val porLinha = if (largo) minOf(kpis.size, if (kpis.size > 5) (kpis.size + 1) / 2 else kpis.size) else 2
    Column(verticalArrangement = Arrangement.spacedBy(if (largo) 14.dp else 10.dp)) {
        kpis.chunked(porLinha.coerceAtLeast(1)).forEach { linha ->
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(if (largo) 14.dp else 10.dp)) {
                linha.forEach { it(Modifier.weight(1f)) }
                repeat(porLinha - linha.size) { Spacer(Modifier.weight(1f)) }
            }
        }
    }
}

/** KPI compacto (cabe 2 por linha no celular). */
@Composable
fun KpiDash(
    titulo: String,
    valor: String,
    modifier: Modifier,
    cor: Color = PortalCores.Navy,
    legenda: String = "",
    destaque: Boolean = false,
    onClick: (() -> Unit)? = null
) {
    val forma = RoundedCornerShape(14.dp)
    Column(
        modifier
            .background(if (destaque) cor.copy(alpha = 0.07f) else PortalCores.Cartao, forma)
            .border(1.dp, if (destaque) cor.copy(alpha = 0.45f) else PortalCores.Borda, forma)
            .let { if (onClick != null) it.clickable(onClick = onClick) else it }
            .padding(horizontal = 16.dp, vertical = 14.dp),
        verticalArrangement = Arrangement.spacedBy(4.dp)
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Box(Modifier.size(9.dp).background(cor, CircleShape))
            Spacer(Modifier.width(8.dp))
            Text(
                titulo, color = PortalCores.TextoForte, style = MaterialTheme.typography.bodyMedium,
                maxLines = 2, overflow = TextOverflow.Ellipsis
            )
        }
        Text(valor, color = cor, fontWeight = FontWeight.Bold, fontSize = 30.sp, maxLines = 1)
        if (legenda.isNotBlank()) {
            Text(legenda, color = PortalCores.CinzaTexto, style = MaterialTheme.typography.bodySmall, maxLines = 2, overflow = TextOverflow.Ellipsis)
        }
    }
}

// ---------- Gráficos ----------

data class SerieDash(val rotulo: String, val cor: Color, val valores: List<Double>)
data class SerieLinhaDash(val rotulo: String, val cor: Color, val valores: List<Double?>)
data class FatiaDash(val rotulo: String, val valor: Double, val cor: Color)

@Composable
private fun Legenda(cor: Color, texto: String, tracejado: Boolean = false, barra: Boolean = false) {
    Row(verticalAlignment = Alignment.CenterVertically) {
        if (barra) {
            Box(Modifier.size(12.dp).background(cor, RoundedCornerShape(3.dp)))
        } else {
            Canvas(Modifier.width(22.dp).height(10.dp)) {
                drawLine(
                    cor, Offset(0f, size.height / 2), Offset(size.width, size.height / 2), 5f,
                    pathEffect = if (tracejado) PathEffect.dashPathEffect(floatArrayOf(8f, 5f)) else null
                )
            }
        }
        Spacer(Modifier.width(6.dp))
        Text(texto, style = MaterialTheme.typography.labelMedium, color = PortalCores.CinzaTexto)
    }
}

@Composable
private fun RotulosEixoX(rotulos: List<String>, recuo: androidx.compose.ui.unit.Dp, grande: Boolean) {
    val passo = when {
        rotulos.size > 24 -> if (grande) 3 else 5
        rotulos.size > 14 -> 2
        else -> 1
    }
    Row(Modifier.fillMaxWidth().padding(start = recuo)) {
        rotulos.forEachIndexed { i, r ->
            Text(
                if (i % passo == 0 || i == rotulos.lastIndex) r else "",
                Modifier.weight(1f), textAlign = TextAlign.Center, maxLines = 1, softWrap = false,
                style = MaterialTheme.typography.labelSmall, color = PortalCores.CinzaTexto
            )
        }
    }
}

private val LarguraEixoDash = 52.dp

@Composable
private fun EixoY(topo: Double, fmt: (Double) -> String, altura: androidx.compose.ui.unit.Dp) {
    Column(
        Modifier.width(LarguraEixoDash - 6.dp).height(altura),
        verticalArrangement = Arrangement.SpaceBetween,
        horizontalAlignment = Alignment.End
    ) {
        listOf(topo, topo / 2, 0.0).forEach {
            Text(fmt(it), style = MaterialTheme.typography.labelSmall, color = PortalCores.CinzaClaro, maxLines = 1)
        }
    }
}

/**
 * Colunas (empilhadas por padrão). [onClick] recebe o índice da coluna clicada; [selecionada] realça uma coluna.
 */
@OptIn(ExperimentalLayoutApi::class)
@Composable
fun GraficoColunas(
    titulo: String,
    subtitulo: String,
    rotulos: List<String>,
    series: List<SerieDash>,
    modifier: Modifier = Modifier.fillMaxWidth(),
    grande: Boolean = false,
    fmt: (Double) -> String = { FmtDash.curto(it) },
    empilhado: Boolean = true,
    meta: Double? = null,
    selecionada: Int? = null,
    onClick: ((Int) -> Unit)? = null
) {
    val n = rotulos.size
    val medidor = rememberTextMeasurer()
    val altura = if (grande) 290.dp else 220.dp
    val totais = (0 until n).map { i ->
        if (empilhado) series.sumOf { it.valores.getOrElse(i) { 0.0 } } else series.maxOfOrNull { it.valores.getOrElse(i) { 0.0 } } ?: 0.0
    }
    val maior = maxOf(totais.maxOrNull() ?: 0.0, meta ?: 0.0)
    val topo = if (maior <= 0) 1.0 else maior * 1.18
    val mostrarRotulos = n <= (if (grande) 31 else 16)
    val estilo = TextStyle(fontSize = if (grande) 12.sp else 10.sp, fontWeight = FontWeight.SemiBold, color = PortalCores.TextoForte)
    CartaoSecao(titulo, modifier, subtitulo) {
        if (n == 0 || totais.all { it <= 0.0 }) {
            Text("Sem registros no período.", color = PortalCores.CinzaClaro, style = MaterialTheme.typography.bodyMedium)
            return@CartaoSecao
        }
        Row(Modifier.fillMaxWidth()) {
            EixoY(topo, fmt, altura)
            Spacer(Modifier.width(6.dp))
            Canvas(
                Modifier.weight(1f).height(altura).let { m ->
                    if (onClick == null) m else m.pointerInput(n) {
                        detectTapGestures { p -> onClick((p.x / (size.width / n.toFloat())).toInt().coerceIn(0, n - 1)) }
                    }
                }
            ) {
                val w = size.width
                val h = size.height
                fun y(v: Double) = (h - (v / topo) * h).toFloat()
                val faixa = w / n
                listOf(0.0, topo / 2, topo).forEach { drawLine(PortalCores.Borda, Offset(0f, y(it)), Offset(w, y(it)), 1.5f) }
                if (selecionada != null && selecionada in 0 until n) {
                    drawRect(PortalCores.AzulClaro, Offset(faixa * selecionada, 0f), Size(faixa, h))
                }
                val larguraTotal = faixa * (if (n > 20) 0.74f else 0.6f)
                for (i in 0 until n) {
                    val esmaecer = selecionada != null && selecionada != i
                    val cx = faixa * (i + 0.5f)
                    if (empilhado) {
                        var base = 0.0
                        series.forEach { s ->
                            val v = s.valores.getOrElse(i) { 0.0 }
                            if (v > 0) {
                                val t = y(base + v)
                                drawRect(
                                    s.cor.copy(alpha = if (esmaecer) 0.35f else 1f),
                                    Offset(cx - larguraTotal / 2, t), Size(larguraTotal, y(base) - t)
                                )
                                base += v
                            }
                        }
                    } else {
                        val lb = larguraTotal / series.size.coerceAtLeast(1)
                        series.forEachIndexed { k, s ->
                            val v = s.valores.getOrElse(i) { 0.0 }
                            if (v > 0) {
                                val t = y(v)
                                drawRect(
                                    s.cor.copy(alpha = if (esmaecer) 0.35f else 1f),
                                    Offset(cx - larguraTotal / 2 + lb * k, t), Size(lb * 0.92f, h - t)
                                )
                            }
                        }
                    }
                    if (mostrarRotulos && totais[i] > 0) {
                        val txt = medidor.measure(fmt(totais[i]), estilo)
                        val esq = (cx - txt.size.width / 2f).coerceIn(0f, (w - txt.size.width).coerceAtLeast(0f))
                        drawText(txt, topLeft = Offset(esq, (y(totais[i]) - txt.size.height - 4f).coerceAtLeast(0f)))
                    }
                }
                meta?.let {
                    drawLine(
                        PortalCores.Vermelho.copy(alpha = 0.85f), Offset(0f, y(it)), Offset(w, y(it)), 3f,
                        pathEffect = PathEffect.dashPathEffect(floatArrayOf(12f, 8f))
                    )
                }
            }
        }
        RotulosEixoX(rotulos, LarguraEixoDash, grande)
        if (series.size > 1 || meta != null || onClick != null) {
            FlowRow(horizontalArrangement = Arrangement.spacedBy(18.dp), verticalArrangement = Arrangement.spacedBy(4.dp)) {
                if (series.size > 1) series.forEach { Legenda(it.cor, it.rotulo, barra = true) }
                meta?.let { Legenda(PortalCores.Vermelho, "Meta ${fmt(it)}", tracejado = true) }
                if (onClick != null) {
                    Text("Clique numa coluna para filtrar", style = MaterialTheme.typography.labelSmall, color = PortalCores.CinzaClaro)
                }
            }
        }
    }
}

/** Linhas com pontos rotulados; [meta] desenha a linha tracejada e colore os pontos (verde/vermelho). */
@OptIn(ExperimentalLayoutApi::class)
@Composable
fun GraficoLinhas(
    titulo: String,
    subtitulo: String,
    rotulos: List<String>,
    series: List<SerieLinhaDash>,
    modifier: Modifier = Modifier.fillMaxWidth(),
    grande: Boolean = false,
    fmt: (Double) -> String = { FmtDash.curto(it) },
    meta: Double? = null,
    menorMelhor: Boolean = true,
    rotularPontos: Boolean = true
) {
    val n = rotulos.size
    val medidor = rememberTextMeasurer()
    val altura = if (grande) 290.dp else 220.dp
    val maiorValor = series.flatMap { it.valores }.filterNotNull().maxOrNull() ?: 0.0
    val topo = maxOf((meta ?: 0.0) * 1.3, maiorValor * 1.2).takeIf { it > 0 } ?: 1.0
    val estilo = TextStyle(fontSize = if (grande) 12.sp else 10.sp, fontWeight = FontWeight.SemiBold)
    val rotular = rotularPontos && n <= (if (grande) 31 else 16)
    CartaoSecao(titulo, modifier, subtitulo) {
        if (n == 0 || series.all { s -> s.valores.all { it == null } }) {
            Text("Sem registros no período.", color = PortalCores.CinzaClaro, style = MaterialTheme.typography.bodyMedium)
            return@CartaoSecao
        }
        Row(Modifier.fillMaxWidth()) {
            EixoY(topo, fmt, altura)
            Spacer(Modifier.width(6.dp))
            Canvas(Modifier.weight(1f).height(altura)) {
                val w = size.width
                val h = size.height
                fun y(v: Double) = (h - (v / topo) * h).toFloat()
                fun x(i: Int) = w * (i + 0.5f) / n
                listOf(0.0, topo / 2, topo).forEach { drawLine(PortalCores.Borda, Offset(0f, y(it)), Offset(w, y(it)), 1.5f) }
                meta?.let {
                    drawLine(
                        PortalCores.Vermelho.copy(alpha = 0.85f), Offset(0f, y(it)), Offset(w, y(it)), 3f,
                        pathEffect = PathEffect.dashPathEffect(floatArrayOf(12f, 8f))
                    )
                }
                series.forEach { s ->
                    var anterior: Offset? = null
                    s.valores.forEachIndexed { i, v ->
                        if (v == null) anterior = null
                        else {
                            val p = Offset(x(i), y(v))
                            anterior?.let { drawLine(s.cor, it, p, 4f) }
                            anterior = p
                        }
                    }
                }
                series.forEach { s ->
                    s.valores.forEachIndexed { i, v ->
                        if (v != null) {
                            val p = Offset(x(i), y(v))
                            val cor = meta?.let { m -> if (if (menorMelhor) v <= m else v >= m) CoresDash.bom else CoresDash.ruim } ?: s.cor
                            drawCircle(Color.White, 7f, p)
                            drawCircle(cor, 7f, p, style = Stroke(4f))
                            if (rotular) {
                                val txt = medidor.measure(fmt(v), estilo.copy(color = cor))
                                val topoTxt = (p.y - txt.size.height - 8f).let { if (it < 0f) p.y + 10f else it }
                                val esq = (p.x - txt.size.width / 2f).coerceIn(0f, (w - txt.size.width).coerceAtLeast(0f))
                                drawText(txt, topLeft = Offset(esq, topoTxt))
                            }
                        }
                    }
                }
            }
        }
        RotulosEixoX(rotulos, LarguraEixoDash, grande)
        FlowRow(horizontalArrangement = Arrangement.spacedBy(18.dp)) {
            series.forEach { Legenda(it.cor, it.rotulo) }
            meta?.let { Legenda(PortalCores.Vermelho, "Meta ${if (menorMelhor) "≤" else "≥"} ${fmt(it)}", tracejado = true) }
        }
    }
}

/** Rosca com total no centro e legenda com % (clicável para filtrar). */
@Composable
fun GraficoRosca(
    titulo: String,
    subtitulo: String,
    fatias: List<FatiaDash>,
    modifier: Modifier = Modifier.fillMaxWidth(),
    grande: Boolean = false,
    fmt: (Double) -> String = { FmtDash.qtd(it) },
    rotuloTotal: String = "total",
    selecionada: String? = null,
    onClick: ((String) -> Unit)? = null
) {
    val validas = fatias.filter { it.valor > 0 }
    val total = validas.sumOf { it.valor }
    val medidor = rememberTextMeasurer()
    val lado = if (grande) 210.dp else 160.dp
    CartaoSecao(titulo, modifier, subtitulo) {
        if (total <= 0) {
            Text("Sem registros no período.", color = PortalCores.CinzaClaro, style = MaterialTheme.typography.bodyMedium)
            return@CartaoSecao
        }
        val corTotal = PortalCores.Navy
        val estiloTotal = TextStyle(fontSize = if (grande) 30.sp else 24.sp, fontWeight = FontWeight.Bold, color = corTotal)
        val estiloRot = TextStyle(fontSize = 11.sp, color = PortalCores.CinzaTexto)
        BoxWithConstraints(Modifier.fillMaxWidth()) {
            val lado2 = maxWidth >= 420.dp
            val grafico: @Composable () -> Unit = {
                Canvas(Modifier.size(lado)) {
                    val esp = size.minDimension * 0.18f
                    val r = Size(size.minDimension - esp, size.minDimension - esp)
                    val tl = Offset(esp / 2, esp / 2)
                    var ini = -90f
                    validas.forEach { f ->
                        val ang = (f.valor / total * 360.0).toFloat()
                        val apagada = selecionada != null && selecionada != f.rotulo
                        drawArc(
                            f.cor.copy(alpha = if (apagada) 0.25f else 1f), ini, (ang - 1.2f).coerceAtLeast(0.6f), false,
                            topLeft = tl, size = r, style = Stroke(esp)
                        )
                        ini += ang
                    }
                    val t1 = medidor.measure(fmt(total), estiloTotal)
                    val t2 = medidor.measure(rotuloTotal, estiloRot)
                    val cy = size.height / 2 - (t1.size.height + t2.size.height) / 2f
                    drawText(t1, topLeft = Offset(size.width / 2 - t1.size.width / 2f, cy))
                    drawText(t2, topLeft = Offset(size.width / 2 - t2.size.width / 2f, cy + t1.size.height))
                }
            }
            val legenda: @Composable (Modifier) -> Unit = { m ->
                Column(m, verticalArrangement = Arrangement.spacedBy(6.dp)) {
                    validas.sortedByDescending { it.valor }.forEach { f ->
                        val sel = selecionada == f.rotulo
                        Row(
                            Modifier
                                .fillMaxWidth()
                                .background(if (sel) PortalCores.AzulClaro else Color.Transparent, RoundedCornerShape(8.dp))
                                .let { if (onClick != null) it.clickable { onClick(f.rotulo) } else it }
                                .padding(horizontal = 6.dp, vertical = 3.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Box(Modifier.size(12.dp).background(f.cor, RoundedCornerShape(3.dp)))
                            Spacer(Modifier.width(8.dp))
                            Text(
                                f.rotulo, Modifier.weight(1f), maxLines = 2, overflow = TextOverflow.Ellipsis,
                                style = MaterialTheme.typography.bodyMedium, color = PortalCores.Texto
                            )
                            Text(fmt(f.valor), fontWeight = FontWeight.SemiBold, color = PortalCores.TextoForte, style = MaterialTheme.typography.bodyMedium)
                            Text(
                                FmtDash.pct(f.valor / total), Modifier.width(52.dp), textAlign = TextAlign.End,
                                style = MaterialTheme.typography.bodySmall, color = PortalCores.CinzaTexto
                            )
                        }
                    }
                }
            }
            if (lado2) {
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(18.dp)) {
                    grafico()
                    legenda(Modifier.weight(1f))
                }
            } else {
                Column(horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.spacedBy(12.dp)) {
                    grafico()
                    legenda(Modifier.fillMaxWidth())
                }
            }
        }
    }
}

/** Ranking em barras horizontais (maiores primeiro). */
@Composable
fun RankingBarras(
    titulo: String,
    subtitulo: String,
    itens: List<Pair<String, Double>>,
    modifier: Modifier = Modifier.fillMaxWidth(),
    cor: Color = PortalCores.AzulIcone,
    fmt: (Double) -> String = { FmtDash.qtd(it) },
    limite: Int = 10,
    corItem: ((String) -> Color)? = null,
    selecionado: String? = null,
    onClick: ((String) -> Unit)? = null
) {
    val lista = itens.filter { it.second > 0 }.sortedByDescending { it.second }
    val mostrar = lista.take(limite)
    val maior = mostrar.maxOfOrNull { it.second }?.takeIf { it > 0 } ?: 1.0
    CartaoSecao(titulo, modifier, subtitulo) {
        if (mostrar.isEmpty()) {
            Text("Sem registros no período.", color = PortalCores.CinzaClaro, style = MaterialTheme.typography.bodyMedium)
            return@CartaoSecao
        }
        mostrar.forEach { (rotulo, valor) ->
            val c = corItem?.invoke(rotulo) ?: cor
            val sel = selecionado == rotulo
            val apagado = selecionado != null && !sel
            Column(
                Modifier
                    .fillMaxWidth()
                    .background(if (sel) PortalCores.AzulClaro else Color.Transparent, RoundedCornerShape(8.dp))
                    .let { if (onClick != null) it.clickable { onClick(rotulo) } else it }
                    .padding(horizontal = 6.dp, vertical = 3.dp),
                verticalArrangement = Arrangement.spacedBy(3.dp)
            ) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Text(
                        rotulo, Modifier.weight(1f), maxLines = 1, overflow = TextOverflow.Ellipsis,
                        style = MaterialTheme.typography.bodyMedium, color = PortalCores.Texto
                    )
                    Text(fmt(valor), fontWeight = FontWeight.SemiBold, color = if (apagado) PortalCores.CinzaClaro else c)
                }
                Box(Modifier.fillMaxWidth().height(10.dp).background(PortalCores.CabecalhoTabela, RoundedCornerShape(5.dp))) {
                    Box(
                        Modifier.fillMaxWidth((valor / maior).toFloat().coerceIn(0.02f, 1f)).height(10.dp)
                            .background(c.copy(alpha = if (apagado) 0.3f else 1f), RoundedCornerShape(5.dp))
                    )
                }
            }
        }
        if (lista.size > limite) {
            Text(
                "+ ${lista.size - limite} outro(s) · ${fmt(lista.drop(limite).sumOf { it.second })}",
                style = MaterialTheme.typography.bodySmall, color = PortalCores.CinzaTexto
            )
        }
    }
}

/** Barras de percentual (0..1) por item, coloridas por faixa: verde ≥ [bom], amarelo ≥ [atencao], vermelho abaixo. */
@Composable
fun BarrasPercentual(
    titulo: String,
    subtitulo: String,
    itens: List<Triple<String, Double, String>>,
    modifier: Modifier = Modifier.fillMaxWidth(),
    bom: Double = 0.9,
    atencao: Double = 0.7
) {
    CartaoSecao(titulo, modifier, subtitulo) {
        if (itens.isEmpty()) {
            Text("Sem registros.", color = PortalCores.CinzaClaro, style = MaterialTheme.typography.bodyMedium)
            return@CartaoSecao
        }
        itens.forEach { (rotulo, v, detalhe) ->
            val c = when {
                v >= bom -> CoresDash.bom
                v >= atencao -> CoresDash.atencao
                else -> CoresDash.ruim
            }
            Column(verticalArrangement = Arrangement.spacedBy(3.dp)) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Text(rotulo, Modifier.weight(1f), maxLines = 1, overflow = TextOverflow.Ellipsis, style = MaterialTheme.typography.bodyMedium, color = PortalCores.Texto)
                    if (detalhe.isNotBlank()) {
                        Text(detalhe, style = MaterialTheme.typography.bodySmall, color = PortalCores.CinzaTexto)
                        Spacer(Modifier.width(10.dp))
                    }
                    Text(FmtDash.pct(v), fontWeight = FontWeight.Bold, color = c)
                }
                Box(Modifier.fillMaxWidth().height(10.dp).background(PortalCores.CabecalhoTabela, RoundedCornerShape(5.dp))) {
                    if (v > 0) Box(Modifier.fillMaxWidth(v.toFloat().coerceIn(0.02f, 1f)).height(10.dp).background(c, RoundedCornerShape(5.dp)))
                }
            }
        }
    }
}
